import ExTable, {ColumnDef, FilterDef, FilterOperator, FilterType} from "../../components/ExTable";
import {useTranslation} from "react-i18next";
import {IdListRequest, useOrganizationStageListQuery, useStageListQuery} from "../../app/api/api";
import {OrganizationStage, StageField} from "../../app/api/model/stage";
import {formatTimestamp} from "../../utils/datetime";
import {IconButton, Tooltip} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {useState} from "react";
import OrganizationAddStageDialog from "./components/OrganizationAddStageDialog";
import Page from "../../components/Layout/Page";
import {usePermission} from "../../hooks/usePermission";
import {PermissionLevel} from "../../app/api/model/user";

interface StageTableProps {
    organizationId: number;
    allStages?: string;
}

export default function StageTable({organizationId, allStages}: StageTableProps) {
    const {t} = useTranslation();
    const [isAddStageDialogOpen, setIsAddStageDialogOpen] = useState(false);
    const {data: stageListResponse} = useStageListQuery({});
    const {hasPermission} = usePermission();

    const columnDefs: ColumnDef<OrganizationStage>[] = [
        {
            fieldName: StageField.IDENTIFIER,
            align: "left",
            label: t("organizations_stageIdentifier"),
            width: 200,
            sortable: true,
            sticky: true,
        },
        {
            fieldName: StageField.DESCRIPTION,
            align: "left",
            label: t("organizations_stageDescription"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: StageField.LOCKED,
            align: "left",
            label: t("generic_locked"),
            width: 200,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return Number(cellValue) ? t("generic_yes") : t("generic_no");
            },
        },
        {
            fieldName: StageField.CREATED_AT,
            align: "left",
            label: t("generic_createdAt"),
            width: 200,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
        {
            fieldName: StageField.UPDATED_AT,
            align: "left",
            label: t("generic_updatedAt"),
            width: 200,
            sortable: true,

            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
    ];

    const filterDefs: FilterDef<OrganizationStage>[] = [
        {
            fieldName: StageField.IDENTIFIER,
            label: t("organizations_stageIdentifier"),
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues:
                stageListResponse?.payload?.map((s) => {
                    return {value: s.identifier, labelKey: s.description};
                }) ?? [],
        },
        {
            fieldName: StageField.DESCRIPTION,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: StageField.LOCKED,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues: [
                {
                    value: "1",
                    labelKey: "generic_yes",
                },
                {
                    value: "0",
                    labelKey: "generic_no",
                },
            ],
        },

        {
            fieldName: StageField.CREATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: StageField.UPDATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
    ];

    return (
        <Page>
            <ExTable<OrganizationStage, IdListRequest>
                data-test={"stageList"}
                queryHook={useOrganizationStageListQuery}
                additionalRequestParams={{id: organizationId}}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                keyFieldName={StageField.IDENTIFIER}
                title={t("generic_stages")}
                renderActions={() => (
                    <>
                        {hasPermission(PermissionLevel.MANAGER) && (
                            <>
                                <Tooltip title={t("action_organizationAddStage")}>
                                    <IconButton
                                        disabled={isAddStageDialogOpen}
                                        onClick={() => setIsAddStageDialogOpen(true)}
                                    >
                                        <AddCircleIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </>
                )}
            />
            <OrganizationAddStageDialog
                open={isAddStageDialogOpen}
                onClose={() => setIsAddStageDialogOpen(false)}
                organizationId={organizationId}
                currentStages={allStages}
            />
        </Page>
    );
}
